import { apiConfig } from './apiConfig';

// Types for User Management
export interface User {
  _id: string;
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // Allow any role string to support database-driven roles
  status: 'active' | 'inactive' | 'suspended';
  department?: string;
  phone?: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  profilePicture?: string;
  twoFactorEnabled?: boolean;
  isActive?: boolean;
  permissions?: string[]; // User-specific permissions (added for admin dashboard)
  /** Which sign-in path this account may use. Absent on legacy records (treat as password). */
  loginMethod?: 'password' | 'microsoft';
}

export interface Role {
  _id: string;
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  _id: string;
  id: string;
  code: string;
  name: string;
  description: string;
  module: string;
  actions: string[];
}

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  department?: string;
  phone?: string;
  /** Which sign-in path the account may use. Defaults to password. */
  loginMethod?: 'password' | 'microsoft';
  /** Required for password accounts; omitted entirely for Microsoft accounts. */
  password?: string;
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  department?: string;
  phone?: string;
  status?: string;
  isActive?: boolean;
  /** Switching to 'microsoft' retires any stored password for the account. */
  loginMethod?: 'password' | 'microsoft';
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  department?: string;
  phone?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface UserResponse {
  success: boolean;
  data: User;
}

export type RolesResponse = Role[];

export type PermissionsResponse = Permission[];

class UserService {
  private baseURL = apiConfig.baseURL;

  // Helper method for API calls
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // User Management APIs
  async getUsers(
    token: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      role?: string;
      status?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    } = {}
  ): Promise<UsersResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `/api/admin/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.makeRequest<UsersResponse>(endpoint, { method: 'GET' }, token);
  }

  async getUser(token: string, userId: string): Promise<UserResponse> {
    return this.makeRequest<UserResponse>(`/api/admin/users/${userId}`, { method: 'GET' }, token);
  }

  async createUser(token: string, userData: CreateUserRequest): Promise<UserResponse> {
    return this.makeRequest<UserResponse>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    }, token);
  }

  async updateUser(token: string, userId: string, userData: UpdateUserRequest): Promise<UserResponse> {
    return this.makeRequest<UserResponse>(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    }, token);
  }

  async deleteUser(token: string, userId: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest(`/api/admin/users/${userId}`, { method: 'DELETE' }, token);
  }

  async resetUserPassword(token: string, userId: string): Promise<{ success: boolean; message: string; temporaryPassword: string }> {
    return this.makeRequest(`/api/admin/users/${userId}/reset-password`, { method: 'POST' }, token);
  }

  async bulkCreateUsers(token: string, users: CreateUserRequest[]): Promise<{ success: boolean; message: string; results: any[] }> {
    return this.makeRequest('/api/admin/users/bulk', {
      method: 'POST',
      body: JSON.stringify({ users }),
    }, token);
  }

  // Role Management APIs
  async getRoles(token: string): Promise<RolesResponse> {
    return this.makeRequest<RolesResponse>('/api/admin/roles', { method: 'GET' }, token);
  }

  async createRole(token: string, roleData: { name: string; description: string; permissions: string[] }): Promise<{ success: boolean; data: Role }> {
    return this.makeRequest('/api/admin/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    }, token);
  }

  async updateRole(token: string, roleId: string, roleData: { name?: string; description?: string; permissions?: string[] }): Promise<{ success: boolean; data: Role }> {
    return this.makeRequest(`/api/admin/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    }, token);
  }

  async deleteRole(token: string, roleId: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest(`/api/admin/roles/${roleId}`, { method: 'DELETE' }, token);
  }

  // Permission Management APIs
  async getPermissions(token: string): Promise<PermissionsResponse> {
    return this.makeRequest<PermissionsResponse>('/api/admin/permissions', { method: 'GET' }, token);
  }

  // Profile Management APIs
  async getProfile(token: string): Promise<{ success: boolean; data: User }> {
    return this.makeRequest('/api/profile/profile', { method: 'GET' }, token);
  }

  async updateProfile(token: string, profileData: UpdateProfileRequest): Promise<{ success: boolean; data: User }> {
    return this.makeRequest('/api/profile/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    }, token);
  }

  async changePassword(token: string, passwordData: ChangePasswordRequest): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/api/profile/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    }, token);
  }

  async uploadProfilePicture(token: string, file: File): Promise<{ success: boolean; data: { profilePictureUrl: string } }> {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response = await fetch(`${this.baseURL}/api/profile/picture`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async enable2FA(token: string): Promise<{ success: boolean; data: { secret: string; qrCode: string } }> {
    return this.makeRequest('/api/profile/2fa/enable', { method: 'POST' }, token);
  }

  async verify2FA(token: string, code: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/api/profile/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ token: code }),
    }, token);
  }

  async disable2FA(token: string, password: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/api/profile/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }, token);
  }

  async getSessions(token: string): Promise<{ success: boolean; data: any[] }> {
    return this.makeRequest('/api/profile/sessions', { method: 'GET' }, token);
  }

  async deleteAccount(token: string, password: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/api/profile', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }, token);
  }

  // Import/Export APIs
  async importUsers(token: string, file: File): Promise<{ success: boolean; message: string; results: any[] }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseURL}/api/admin/users/import`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async exportUsers(token: string, format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
    const response = await fetch(`${this.baseURL}/api/admin/users/export?format=${format}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.blob();
  }
}

export const userService = new UserService();